import React, { JSX } from 'react'
import loadable from '@loadable/component'
import {
  NODE_PARAGRAPH,
  NODE_HEADING,
  MARK_LINK,
  NODE_CODEBLOCK,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  NODE_QUOTE,
  NODE_HR,
  MARK_CODE,
  NODE_IMAGE,
  BlockResolvers,
  MarkResolvers,
  StoryblokRichtext
} from 'storyblok-rich-text-react-renderer'
import clsx from 'clsx'
import kebabCase from 'lodash.kebabcase'
import { Language } from 'prism-react-renderer'
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { ArticleCTA, ArticleCTAProps } from '@/components/molecules/ArticleCTA/ArticleCTA'
import { Video } from '@/components/molecules/Video/Video'
import { removeFoldersFromUrl } from '@/helpers/removeFoldersFromUrl'
import { withTrailingSlash } from '@/helpers/withTrailingSlash'
import { TableTypes } from '@/components/molecules/Table/types'
import { getStoryblokImageSize } from '@/helpers/getStoryblokImageSize'
import { OverviewBlock } from '@/components/molecules/OverviewBlock/OverviewBlock'
import { QuoteBlock } from '@/components/molecules/QuoteBlock/QuoteBlock'
import { CaseStudyCTA } from '@/components/molecules/CaseStudyCTA/CaseStudyCTA'
import { CaseStudyAuthor } from '@/components/molecules/CaseStudyAuthor/CaseStudyAuthor'
import { RichTextField } from './RichTextField'
import * as styles from './RichTextField.module.css'

const CodeBlock = loadable(() => import('@/components/molecules/CodeBlock/CardBlock'), {
  resolveComponent: (component) => component.CodeBlock
})
const Table = loadable(() => import('@/components/molecules/Table/Table'), {
  resolveComponent: (component) => component.Table
})

const firstCharacter = (children: string[]) => {
  const data = [...children]
  if (data.length && ![',', '.', ':', '?', '!', ')', ']', '/'].includes(data[0][0])) {
    data[0] = `\u00A0${data[0]}`
  }
  return data
}

export const blokResolvers: { [k: string]: (props: any) => JSX.Element } = {
  component_link: ({ rel, is_external, new_tab, link_url, text, customCssClass }: CustomLinkTypes) => (
    <CustomLink
      rel={rel}
      is_external={is_external}
      new_tab={new_tab}
      link_url={link_url}
      className="inline-block"
      customCssClass={customCssClass}
    >
      {text}
    </CustomLink>
  ),
  component_video: ({ url, thumbnail }: VideoTypes) => <Video url={url} thumbnail={thumbnail} />,
  component_article_cta: ({ title, description, action_button }: ArticleCTAProps) => (
    <ArticleCTA title={title} description={description} action_button={action_button} />
  ),
  component_table: ({ table }: { table: TableTypes }) => <Table {...table}>TEST</Table>,
  component_anchor: ({ text, id, className, attributes }: any) => (
    <a href={id} className={clsx('block mb-20 hover:text-primary-500', className)} {...attributes}>
      {text}
    </a>
  ),
  component_note: ({ type, title, body }: { type: string; title: string; body: StoryblokRichtext | undefined }) => (
    <div
      className={clsx(
        'px-28 py-20 !text-noteText rounded-lg mb-28',
        type === 'success' && 'bg-green-noteBg before:bg-green-noteText',
        type === 'danger' && 'bg-red-noteBg before:bg-red-noteText',
        type === 'neutral' && 'bg-gray-noteBg before:bg-gray-noteText',
        type === 'warning' && 'bg-yellow-noteBg before:bg-yellow-noteText',
        styles.noteWrapper
      )}
    >
      <p
        className={clsx(
          'text-bodyM font-bold mb-12',
          type === 'success' && '!text-green-noteText',
          type === 'danger' && '!text-red-noteText',
          type === 'neutral' && '!text-gray-noteText',
          type === 'warning' && '!text-yellow-noteText'
        )}
      >
        {title}
      </p>
      <div
        className={clsx(styles.note, {
          [styles.success]: type === 'success',
          [styles.danger]: type === 'danger',
          [styles.neutral]: type === 'neutral',
          [styles.warning]: type === 'warning'
        })}
      >
        <RichTextField data={body} />
      </div>
    </div>
  ),
  component_overview_block: (props) => <OverviewBlock {...props} />,
  component_quote_block: (props) => <QuoteBlock {...props} />,
  component_case_study_cta: (props) => <CaseStudyCTA {...props} />,
  component_case_study_author: (props) => <CaseStudyAuthor {...props} />
}

export const nodeResolvers: Partial<BlockResolvers> = {
  [NODE_PARAGRAPH]: (children: any) => (children ? <p className="richtext-content">{children}</p> : <br />),
  [NODE_HEADING]: (children: any, { level, id }: { level: number; id: string }) => {
    const headings = { h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6' }
    // @ts-ignore
    const HeadingComponent = headings[`h${level}`]

    // eslint-disable-next-line react/destructuring-assignment
    const { props } = children[0]
    const hrefId = level === 3 && props ? kebabCase(props.children) : kebabCase(children)
    return (
      <HeadingComponent
        className={clsx(
          `relative font-poppins text-h${level}mobile tablet:text-h${level} font-bold my-32 richtext-content ${id} richTextHeading group`,
          styles.heading
        )}
        id={hrefId}
      >
        <a href={`#${hrefId}`} className="absolute -translate-x-full pr-8 hidden group-hover:block">
          #
        </a>
        {children}
      </HeadingComponent>
    )
  },
  preceding_paragraph: (children: any) =>
    children ? <p className="inline-block richtext-content">{children}&nbsp;</p> : null,
  following_paragraph: (children: any) =>
    children ? <p className="inline-block richtext-content">{firstCharacter(children)};</p> : null,
  [NODE_CODEBLOCK]: (children: string[], { class: className }: { class: string }) => {
    // @ts-ignore:next-line
    const language: Language = className?.split('-')[1]
    // eslint-disable-next-line react/destructuring-assignment
    return <CodeBlock language={language} code={children[0]} />
  },
  [NODE_LI]: (children: any) => (
    <li className={clsx(styles.listItem, 'richtext-content font-mulish mb-16')}>{children}</li>
  ),
  [NODE_OL]: (children: any) => <ol className="list-decimal list-outside my-32 ml-20 font-mulish">{children}</ol>,
  [NODE_UL]: (children: any) => <ul className="list-disc list-outside my-32 ml-20 font-mulish">{children}</ul>,
  [NODE_QUOTE]: (children: any) => (
    <div className="border-l-4 border-grey-100 py-12 pl-20 italic my-32">{children}</div>
  ),
  [NODE_HR]: () => <hr className="border-grey-100 my-32" />,
  [NODE_IMAGE]: (
    _: any,
    { src, alt, loading }: { src: string; alt: string; loading?: 'eager' | 'lazy' | undefined }
  ) => {
    const { width, height } = getStoryblokImageSize(src)

    return (
      <picture>
        <source media="(max-width: 375px)" srcSet={`${src}/m/300x0`} />
        <source media="(max-width: 480px)" srcSet={`${src}/m/480x0`} />
        <source media="(max-width: 640px)" srcSet={`${src}/m/640x0`} />
        <source media="(max-width: 768px)" srcSet={`${src}/m/700x0`} />
        <source media="(max-width: 1024px)" srcSet={`${src}/m/960x0`} />
        <source media="(max-width: 1100px)" srcSet={`${src}/m/1000x0`} />
        <source media="(max-width: 1440px)" srcSet={`${src}/m/600x0`} />
        <img src={`${src}/m/600x0`} width={width} height={height} alt={alt} loading={loading || 'lazy'} />
      </picture>
    )
  }
}

export const markResolvers: Partial<MarkResolvers> = {
  [MARK_LINK]: (
    children: any,
    { linktype, href, target }: { linktype: 'email' | 'story'; href: string; target: string }
  ) => {
    const hrefValues = { email: `mailto:${href}`, story: withTrailingSlash(removeFoldersFromUrl(href)), url: href }
    const url = hrefValues[linktype] || href

    return (
      <a href={url} target={target} className="text-primary-600 hover:text-primary-500 underline underline-offset-2">
        {children}
      </a>
    )
  },
  [MARK_CODE]: (children: any) => (
    <code className="inline bg-grey-100 p-4 rounded font-sans font-medium">{children}</code>
  )
}
